<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        
        <template #container>
            <div class="dialog-header">
                Transfer Ownership
            </div>
            <div class="dialog-content">

                <div class="flex flex-column gap-1 mb-3">
                    <span class="text">You want to transfer the ownership of the following licenses:</span>
                    
                    <DataTable :value="selectedLicenses" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-3" scrollable scrollHeight="flex" style="max-height: 25vh;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{ slotProps.data.serial_number }}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application"></Column>
                        <Column field="product.exp" header="Expiration">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.product.exp === '-1'" class="font-italic">None</span>
                                <span v-else>{{format(new Date(slotProps.data.product.exp), 'MMM do, yyyy')}}</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="flex flex-column gap-2">

                    <div class="flex flex-column">

                        <div class="w-full flex flex-row justify-content-between align-items-center">
                            <div class="subtitle p-0 m-0 w-full justify-self-start flex flex-row align-items-center justify-content-start gap-2">
                                <span>Choose the new owner</span>
                                <Button class="m-0 p-0" rounded text @click="showAddNewUserDialog = true" style="height: 100%; border-radius: 980px;">
                                    <SvgPlusCircle className="pluscircle-svg" color="var(--button-normal-color)"/>
                                </Button>
                            </div>
                        </div>

                        <div v-if="allUsersSorted" class="mt-2" style="border: 1px solid var(--table-border-color); width: 100%; border-radius: var(--my-border-radius);">
                            <ScrollPanel style="max-height: 15rem; height: 15rem; min-height: 0px; width: 100%;" class="px-1">
                                <div style="padding: 0px; width: 100%;">
                                    <DataView :value="allUsersSorted"  class="w-full px-1 my-1">
                                        <template #empty>
                                            <div class="w-full flex flex-row justify-content-start align-items-center py-1" style="background-color: var(--table-row-color);">
                                                <span>You haven't added any users yet. Get started by using the button above or do it through the <span style="font-weight: var(--font-weight-bold);">Users</span> panel.</span>
                                            </div>
                                        </template>
                                        <template #list="slotProps">
                                            <div class="grid grid-nogutter">
                                                <div v-for="(item, index) in slotProps.items" :key="index" class="px-0 delegates-item flex flex-row align-items-center gap-2 w-full" style="padding: 0.3rem 0.5rem; font-size: small; color: var(--theme-font-color);">
                                                    <Checkbox :binary="true" :modelValue="selectedUserId && selectedUserId==item.id" @click="clickedOnUserWithId(item.id)"/>
                                                    <span class="subtext">{{item.first_name}} {{item.last_name}} ({{item.email}})</span>
                                                </div>
                                            </div>
                                        </template>
                                    </DataView>
                                </div>
                            </ScrollPanel>
                        </div>
                    </div>
                </div>
            </div>
            <div class="dialog-footer">
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="emit('close')"/>
                    <ButtonModule type="primary" severity="normal" label="Transfer" @click="showPasswordVerificationDialog=true" :disabled="!selectedUserId"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Authentication using PASSWORD -->
    <VerificationAuthenticationDialog :showDialog="showPasswordVerificationDialog" :incorrectPassword="wrongPassword" @cancel="showPasswordVerificationDialog = false;" @ok="(val) => verificationAuthenticationDone(val)" :isLoading="httpTransferOwnershipLoading"/>

    <!-- Add a New User window -->
    <AddNewUserDialog :showDialog="showAddNewUserDialog" @close="showAddNewUserDialog = false" @added-new-user="(userInfo) => addedUser(userInfo)"/>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog'
import DataTable from 'primevue/datatable'
import Button from 'primevue/button'
import Column from 'primevue/column'
import ScrollPanel from 'primevue/scrollpanel'
import DataView from 'primevue/dataview'
import Checkbox from 'primevue/checkbox'
// vue
import ButtonModule from '@/components/Modules/ButtonModule'
import AddNewUserDialog from '@/components/Dialogs/Actions/AddNewUserDialog.vue'
import SvgPlusCircle from '@/components/SVGs/SvgPlusCircle.vue';
import VerificationAuthenticationDialog from '@/components/Dialogs/OtherDialogs/VerificationAuthenticationDialog.vue';
// Others
import toastService from '@/composables/toastService';
import { format } from 'date-fns';
import UserUtils from '@/composables/UserUtils';
import UsersUtils from '@/composables/UsersUtils';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)
const { allUsers } = UsersUtils(store)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchTransferOwnership } = useApiRequest()
const httpTransferOwnershipLoading = ref(false)
const httpTransferOwnershipData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close', 'refresh-licenses-table'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    selectedLicenses: Array
})
const { showDialog, selectedLicenses } = toRefs(props)

// --- VARIABLES ---
const allUsersSorted = computed(() => {
    return allUsers.value.sort((a,b) => { // SORT IN THE ALPHABETICAL ORDER
        const nameA = a.first_name.toLowerCase() + a.last_name.toLowerCase()
        const nameB = b.first_name.toLowerCase() + b.last_name.toLowerCase()
        if (nameA < nameB) { return -1 }
        else if (nameA > nameB) { return 1 }
        else { return 0 }
    })
})

// --- Selected user ---
const selectedUserId = ref(null)
const clickedOnUserWithId = (index) => {
    selectedUserId.value = (selectedUserId.value == index) ? null : index
}

// - Add a New User -
const showAddNewUserDialog = ref(false)
const addedUser = (userInfo) => {
    selectedUserId.value = userInfo.id
}

// --- PASSWORD VERIFICATION ---
const showPasswordVerificationDialog = ref(false)
const wrongPassword = ref(false)
// Whenever the dialog opens, it should reset the wrongPassword boolean
watchEffect(() => {
    if (showPasswordVerificationDialog.value) { wrongPassword.value = false }
})
const verificationAuthenticationDone = (password) => {
    // IT CALLS THE SUBMIT REQUEST WITH THE PASSWORD AS THE VERIFICATION
   submitTransferOwnershipConfirmed(password)
}

// --- SUBMIT DELETE SERIAL CONFIRMED ---
const submitTransferOwnershipConfirmed = async (passwordVerification) => {
    if (!selectedUserId.value) {
        toastService.add({ severity: 'error', summary: `Transfer Ownership: Unexpected issue. (1)`, detail: `Please refresh the page and try again. If the issue persists, contact us.`, life: 8000 });
        return;
    }
    wrongPassword.value = false
    // Send HTTP request
    httpTransferOwnershipLoading.value = true
    try {
        httpTransferOwnershipData.value = await fetchTransferOwnership({ password_plain_auth: passwordVerification, serial_numbers: selectedLicenses.value.map(license => license.serial_number), new_owner_id: selectedUserId.value })
        // Reset of values
        showPasswordVerificationDialog.value = false
        wrongPassword.value = false
        // Refresh of data
        if (httpTransferOwnershipData.value.licenses.length < 1) {
            toastService.add({ severity: 'error', summary: `Transfer Ownership: Unexpected issue. (2)`, detail: `Please refresh the page and try again. If the issue persists, contact us.`, life: 8000 });
        } else {
            for (let i = 0; i < httpTransferOwnershipData.value.licenses.length; i++) {
                store.dispatch('licenses/updateLicense', httpTransferOwnershipData.value.licenses[i])
            }
            emit("close")
        }
    } catch (err) {
        handleTransferOwnershipError(err)
    } finally {
        httpTransferOwnershipLoading.value = false
    }
}
// Handle an error from the server
const handleTransferOwnershipError = (error) => {
    try {  
      if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
          toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
          return;
      }
      const errorStatus = error.response.status
      const errorCode = error.response.data.error_code
      const errorMessage = error.response.data.error_message
      switch (errorStatus) {
        case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM / INPUTS_INVALID (400)
            if (errorCode == 'ACCESS_TOKEN_MISSING') {
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                // Logout
                logoutUser()
            }
            else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                toastService.add({ severity: 'warn', summary: "Transfer Ownership: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / WRONG_PASSWORD (401)
            if (errorCode == 'WRONG_PASSWORD') { // WRONG_PASSWORD
                wrongPassword.value = true
            }    
            else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                // Logout: Do nothing, the interceptor should have made a refresh token request!
                // logoutUser()
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 404: // SERIAL_INVALID / ACCOUNT_NOT_FOUND / NEW_OWNER_ACCOUNT_NOT_FOUND (404) => toast + logout
            if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Invalid Serial Number.", detail: 'The license is invalid and has been deleted from the table.', life: 5000 });
                store.commit('licenses/removeLicense', license.value.serial_number)
                emit('refresh-licenses-table')
                emit('close-license-window')
            }
            else if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                // Logout
                logoutUser()
            }
            else if (errorCode == 'NEW_OWNER_ACCOUNT_NOT_FOUND') { // NEW_OWNER_ACCOUNT_NOT_FOUND
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Other User Not Found.", detail: 'The account of the user you want to transfer to does not exist. Please refresh and try again.', life: 8000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 409: // USER_NOT_OWNER (409)
            if (errorCode == 'USER_NOT_OWNER') { // USER_NOT_OWNER
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Not Authorized.", detail: errorMessage, life: 5000 });
                emit('refresh-licenses-table')
                emit('close-license-window')
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
            if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        default:
            toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
            break;
      }
    } catch {
        toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Support link ---
const transferOwnershipZendeskLink = "https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(transferOwnershipZendeskLink);
    }
}
</script>

<style scoped>
:deep(.p-checkbox .p-checkbox-box .pi) {
    transform: scale(0.8);
    font-weight: 50;
}
:deep(.p-checkbox.checkbox-mixed .p-checkbox-box) {
    background-color: var(--checkbox-mixed-background-color) !important;
}
</style>